import { uptimeFigure } from "../../lib/dashboard";
import { count, dateOnly } from "../../lib/format";
import { TONE_BG, statusTone } from "../../lib/status";
import type { FleetStats } from "../../lib/types";
import { MicroLabel, SectionTitle } from "../ui/Text";
import { Figure } from "./Figure";
import { PanelLink } from "./PanelLink";

/**
 * The fleet now: connector states as one proportional bar, and how much of the
 * recent past it was reachable.
 *
 * The bar rather than a table of counts because the question is the share — "is
 * most of the fleet free" — and a bar answers it before the numbers are read. The
 * counts are printed under it anyway, since a share hides that one faulted
 * connector in a fleet of two hundred is still one somebody must visit.
 */
export function FleetPanel({ fleet, zone }: { readonly fleet: FleetStats; readonly zone: string }) {
  const statuses = Object.entries(fleet.connectors.byStatus).sort(([, a], [, b]) => b - a);
  const { uptime } = fleet;

  return (
    <section aria-labelledby="fleet-title" className="flex flex-col gap-4">
      <SectionTitle action={<PanelLink to="/fleet">Charge points</PanelLink>}>
        <span id="fleet-title">Fleet</span>
      </SectionTitle>

      <div className="flex flex-col gap-2">
        <MicroLabel>{count(fleet.connectors.total)} connectors</MicroLabel>
        {fleet.connectors.total === 0 ? null : (
          <div className="flex h-2 w-full overflow-hidden bg-paper-sunken" aria-hidden="true">
            {statuses.map(([status, total]) => (
              <span
                key={status}
                className={TONE_BG[statusTone(status)]}
                style={{ width: `${(total / fleet.connectors.total) * 100}%` }}
              />
            ))}
          </div>
        )}
        <dl className="m-0 flex flex-wrap gap-x-5 gap-y-1">
          {statuses.map(([status, total]) => (
            <div key={status} className="flex items-baseline gap-1.5">
              <span
                className={`block size-2 self-center ${TONE_BG[statusTone(status)]}`}
                aria-hidden="true"
              />
              <dt className="text-data text-ink-muted">{status}</dt>
              <dd className="m-0 text-data font-semibold text-ink tabular-nums">{count(total)}</dd>
            </div>
          ))}
        </dl>
      </div>

      <dl className="m-0 grid grid-cols-3 gap-4">
        <Figure label="Uptime 24 h" value={uptimeFigure(uptime.last24h?.ratio ?? null)} unit="%" />
        <Figure label="Uptime 7 d" value={uptimeFigure(uptime.last7d?.ratio ?? null)} unit="%" />
        <Figure label="Reboots 24 h" value={count(fleet.rebootsLast24h)} />
      </dl>
      {/* Uptime has no history before the connection log began, and a 7-day
          figure over two days of records must say so rather than look complete. */}
      {uptime.collectingSince === null ? (
        <p className="m-0 text-data text-ink-faint">No connection history recorded yet.</p>
      ) : (
        <p className="m-0 text-data text-ink-faint">
          Uptime counted from {dateOnly(uptime.collectingSince, zone)}, accepted stations only.
        </p>
      )}
    </section>
  );
}
